/**
 * FieldLabel — label + optional required marker + optional hint icon, driven
 * by an i18n key prefix.
 *
 * Resolves three keys from the same prefix (see `getFieldPlaceholder`):
 *
 *   <i18nKey>.label        (required — visible label)
 *   <i18nKey>.hint         (optional — info-icon tooltip)
 *   <i18nKey>.placeholder  (optional — read by callers via getFieldPlaceholder)
 *
 * If `.hint` is missing in the bundle, `t()` returns the key verbatim — we
 * detect that and skip the tooltip rather than rendering a raw key string.
 *
 * Usage:
 *   <FieldLabel i18nKey="process.field.manual.checklist" required />
 *
 * The placeholder resolver lives in `./fieldI18n` so consumers can call
 * `getFieldPlaceholder(prefix, t)` when wiring an Input — keeps the
 * label/hint/placeholder triplet in lockstep without prop drilling.
 *
 * Moved here from `apps/process-designer/inspector/shared/FieldLabel.tsx`
 * (Wave 7 E10 toolkit dedupe) so it's reusable platform-wide.
 */
import { makeStyles, tokens } from '@fluentui/react-components';
import { useLocale } from '../i18n';
import { HintTooltip } from './HintTooltip';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    alignItems: 'center',
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    marginBottom: '2px',
  },
  required: {
    color: tokens.colorPaletteRedForeground1,
    marginLeft: '2px',
  },
});

export interface FieldLabelProps {
  /** i18n key prefix — `.label` / `.hint` / `.placeholder` are appended. */
  i18nKey: string;
  required?: boolean;
  /** Override the resolved label text — escape hatch for dynamic strings. */
  labelOverride?: string;
}

export function FieldLabel({ i18nKey, required, labelOverride }: FieldLabelProps) {
  const styles = useStyles();
  const { t } = useLocale();
  const labelKey = `${i18nKey}.label`;
  const hintKey = `${i18nKey}.hint`;
  const label = labelOverride ?? t(labelKey);
  const hint = t(hintKey);
  const hasHint = hint !== hintKey && hint.trim() !== '';

  return (
    <label className={styles.root}>
      {label}
      {required && <span className={styles.required}>*</span>}
      {hasHint && <HintTooltip text={hint} />}
    </label>
  );
}
